import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FolderIcon from '@mui/icons-material/Folder';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const Chats = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedProjectUuid, setSelectedProjectUuid] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
  });
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [moveChatDialog, setMoveChatDialog] = useState({ open: false, chat: null });

  useEffect(() => {
    fetchProjects();
    fetchFolders();
    // Получаем project_uuid из URL параметров
    const urlParams = new URLSearchParams(window.location.search);
    const projectUuid = urlParams.get('project_uuid');
    if (projectUuid) {
      setSelectedProjectUuid(projectUuid);
    }
  }, []);

  useEffect(() => {
    if (selectedProjectUuid || projects.length > 0) {
      fetchChats();
      // Обновляем список чатов каждые 5 секунд для проверки подключения
      const interval = setInterval(fetchChats, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedProjectUuid, projects]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      const fetchedProjects = response.data.projects || [];
      setProjects(fetchedProjects);
      
      // Если project_uuid не выбран, выбираем первый проект
      if (!selectedProjectUuid && fetchedProjects.length > 0) {
        setSelectedProjectUuid(fetchedProjects[0].uuid);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchFolders = async () => {
    try {
      const url = selectedProjectUuid 
        ? `/api/folders?project_uuid=${selectedProjectUuid}`
        : '/api/folders';
      const response = await axios.get(url);
      setFolders(response.data.folders || []);
    } catch (err) {
      console.error('Error fetching folders:', err);
    }
  };

  const fetchChats = async () => {
    try {
      const url = selectedProjectUuid 
        ? `/api/chats?project_uuid=${selectedProjectUuid}`
        : '/api/chats';
      const response = await axios.get(url);
      setChats(response.data.chats);
    } catch (err) {
      console.error('Error fetching chats:', err);
    }
  };

  const handleOpen = () => {
    if (!selectedProjectUuid) {
      setError('Сначала выберите проект');
      return;
    }
    setOpen(true);
    setFormData({ name: '', description: '', project_uuid: selectedProjectUuid });
    setError('');
    setConnectionInfo(null);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
    setConnectionInfo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/api/chats', {
        ...formData,
        project_uuid: selectedProjectUuid,
      });
      setConnectionInfo({
        uuid: response.data.chat.uuid,
        message: response.data.message,
      });
      fetchChats();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка создания чата');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (uuid) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот чат?')) {
      return;
    }

    try {
      await axios.delete(`/api/chats/${uuid}`);
      setSuccess('Чат удален');
      fetchChats();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка удаления чата');
    }
  };

  const handleCopyUUID = (uuid) => {
    const command = `connect ${uuid}`;
    navigator.clipboard.writeText(command);
    setSnackbar({ open: true, message: 'Команда скопирована в буфер обмена' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'connected':
        return 'Подключен';
      case 'pending':
        return 'Ожидание подключения';
      default:
        return status;
    }
  };

  // Проверка, является ли пользователь владельцем или менеджером выбранного проекта
  const canManageProject = () => {
    if (!selectedProjectUuid || !user) return false;
    const project = projects.find(p => p.uuid === selectedProjectUuid);
    if (!project) return false;
    // Для проверки менеджера нужно будет получать информацию о членстве в проекте
    // Пока проверяем только владельца и админов
    return (
      user.role === 'admin' ||
      user.role === 'super_admin' ||
      project.created_by_uuid === user.uuid
    );
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Управление чатами
          </Typography>
          {user?.role === 'admin' && (
            <Button variant="contained" onClick={handleOpen}>
              Создать чат
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {chats.map((chat) => (
            <Grid item xs={12} md={6} key={chat.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">{chat.name}</Typography>
                      {chat.folder_name && (
                        <Chip
                          icon={<FolderIcon />}
                          label={chat.folder_name}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                    {canManageProject() && (
                      <Chip
                        label={getStatusLabel(chat.status)}
                        color={getStatusColor(chat.status)}
                        size="small"
                      />
                    )}
                  </Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    paragraph
                    sx={{ minHeight: chat.description ? 'auto' : '20px', mb: chat.description ? 1 : 0 }}
                  >
                    {chat.description || '\u00A0'}
                  </Typography>
                  {canManageProject() && (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>UUID:</strong> {chat.uuid}
                      </Typography>
                      {chat.status === 'pending' && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Отправьте в чат Telegram:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                bgcolor: 'background.paper',
                                p: 1,
                                borderRadius: 1,
                                flex: 1,
                              }}
                            >
                              connect {chat.uuid}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyUUID(chat.uuid)}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                      {chat.status === 'connected' && chat.connected_at && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Подключен: {new Date(chat.connected_at).toLocaleString('ru-RU')}
                        </Typography>
                      )}
                    </>
                  )}
                  {chat.created_by_username && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Создан: {chat.created_by_username}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    {canManageProject() && (
                      <>
                        <Button
                          size="small"
                          startIcon={<FolderIcon />}
                          onClick={() => setMoveChatDialog({ open: true, chat })}
                        >
                          {chat.folder_name ? 'Изменить папку' : 'В папку'}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(chat.uuid)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {chats.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Нет созданных чатов
            </Typography>
          </Box>
        )}

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>Создать новый чат</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {connectionInfo ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Чат успешно создан!
                  </Alert>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Отправьте в чат Telegram следующее сообщение:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        flex: 1,
                      }}
                    >
                      connect {connectionInfo.uuid}
                    </Typography>
                    <IconButton
                      onClick={() => handleCopyUUID(connectionInfo.uuid)}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    После отправки команды чат будет автоматически подключен.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Название чата"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label="Описание чата"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    margin="normal"
                    multiline
                    rows={3}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              {connectionInfo ? (
                <Button onClick={handleClose} variant="contained">
                  Закрыть
                </Button>
              ) : (
                <>
                  <Button onClick={handleClose}>Отмена</Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? 'Создание...' : 'Создать'}
                  </Button>
                </>
              )}
            </DialogActions>
          </form>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />

        {/* Диалог перемещения чата в папку */}
        <Dialog 
          open={moveChatDialog.open} 
          onClose={() => setMoveChatDialog({ open: false, chat: null })} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>Переместить чат в папку</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Выберите папку</InputLabel>
              <Select
                value={moveChatDialog.chat?.folder_id || 'no-folder'}
                onChange={async (e) => {
                  const folderId = e.target.value === 'no-folder' ? null : e.target.value;
                  try {
                    await axios.post(`/api/folders/${folderId || 'null'}/move-chat/${moveChatDialog.chat.uuid}`);
                    setSuccess('Чат успешно перемещен');
                    setMoveChatDialog({ open: false, chat: null });
                    fetchChats();
                  } catch (err) {
                    setError(err.response?.data?.error || 'Ошибка перемещения чата');
                    setMoveChatDialog({ open: false, chat: null });
                  }
                }}
              >
                <MenuItem value="no-folder">Без папки</MenuItem>
                {folders.map(folder => (
                  <MenuItem key={folder.id} value={folder.id}>{folder.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMoveChatDialog({ open: false, chat: null })}>Отмена</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Chats;
